import React, { useState } from 'react';
import axios from 'axios';
import { Send, Phone, Mail, MapPin, CheckCircle2, AlertCircle } from 'lucide-react';
import { SITE, SERVICES } from '../data/site';
import { TESTIDS } from '../constants/testIds';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = {
  name: '',
  phone: '',
  email: '',
  postcode: '',
  service: '',
  message: '',
};

export const Contact = () => {
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState({ state: 'idle', msg: '' });

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.message.trim()) {
      setStatus({ state: 'error', msg: 'Please fill in your name, phone and message.' });
      return;
    }
    setStatus({ state: 'loading', msg: '' });
    try {
      await axios.post(`${API}/contact`, form);
      setStatus({
        state: 'success',
        msg: `Thanks ${form.name.split(' ')[0]}! Luke will be in touch shortly.`,
      });
      setForm(initial);
    } catch (err) {
      setStatus({
        state: 'error',
        msg:
          err?.response?.data?.detail ||
          'Sorry, something went wrong. Please call Luke directly on ' + SITE.phoneDisplay + '.',
      });
    }
  };

  return (
    <section
      id="contact"
      data-testid={TESTIDS.contactSection}
      className="py-20 sm:py-28 bg-[#050505] relative"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10">
          {/* Left — info panel */}
          <div className="lg:col-span-5">
            <span className="font-display uppercase tracking-[0.4em] text-xs text-[#E8B842]">
              / Get in touch
            </span>
            <h2 className="font-display uppercase font-extrabold text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95] mt-4">
              Contact <span className="text-[#E8B842]">Luke</span> today.
            </h2>
            <p className="mt-6 text-[#A3A3A3] text-base sm:text-lg leading-relaxed">
              Free, no-obligation quotes across Leamington Spa and Warwickshire. The quickest
              way to get a price is a quick call — or fill in the form and Luke will get back
              to you as soon as possible.
            </p>

            <div className="mt-10 space-y-4">
              <a
                href={SITE.phoneTel}
                data-testid="contact-phone-link"
                className="group flex items-center gap-4 p-4 border border-[#E8B842]/20 hover:border-[#E8B842] bg-[#0E0E0E] transition-colors"
              >
                <div className="w-12 h-12 bg-[#E8B842] text-black flex items-center justify-center">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs font-display uppercase tracking-widest text-[#A3A3A3]">
                    Call Luke
                  </div>
                  <div className="font-display uppercase font-bold text-white text-xl group-hover:text-[#E8B842] transition-colors">
                    {SITE.phoneDisplay}
                  </div>
                </div>
              </a>

              <a
                href={SITE.emailHref}
                data-testid="contact-email-link"
                className="group flex items-center gap-4 p-4 border border-[#E8B842]/20 hover:border-[#E8B842] bg-[#0E0E0E] transition-colors"
              >
                <div className="w-12 h-12 bg-[#E8B842] text-black flex items-center justify-center">
                  <Mail className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <div className="text-xs font-display uppercase tracking-widest text-[#A3A3A3]">
                    Email
                  </div>
                  <div className="font-display uppercase font-bold text-white text-sm sm:text-base group-hover:text-[#E8B842] transition-colors break-all">
                    {SITE.email}
                  </div>
                </div>
              </a>

              <div className="flex items-center gap-4 p-4 border border-[#E8B842]/20 bg-[#0E0E0E]">
                <div className="w-12 h-12 bg-[#E8B842] text-black flex items-center justify-center">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs font-display uppercase tracking-widest text-[#A3A3A3]">
                    Based in
                  </div>
                  <div className="font-display uppercase font-bold text-white text-xl">
                    {SITE.location}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right — form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid={TESTIDS.contactForm}
              className="bg-[#0E0E0E] border border-[#E8B842]/20 p-6 sm:p-10"
            >
              <h3 className="font-display uppercase font-bold text-white text-2xl mb-2">
                Request a free quote
              </h3>
              <p className="text-sm text-[#A3A3A3] mb-8">
                Tell us a bit about the job — the more detail, the more accurate your quote.
              </p>

              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Your name *">
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={update('name')}
                    data-testid={TESTIDS.contactName}
                    placeholder="John Smith"
                    className={inputClass}
                  />
                </Field>

                <Field label="Phone *">
                  <input
                    type="tel"
                    required
                    value={form.phone}
                    onChange={update('phone')}
                    data-testid={TESTIDS.contactPhone}
                    placeholder="07xxx xxx xxx"
                    className={inputClass}
                  />
                </Field>

                <Field label="Email">
                  <input
                    type="email"
                    value={form.email}
                    onChange={update('email')}
                    data-testid={TESTIDS.contactEmail}
                    placeholder="you@example.com"
                    className={inputClass}
                  />
                </Field>

                <Field label="Postcode">
                  <input
                    type="text"
                    value={form.postcode}
                    onChange={update('postcode')}
                    data-testid={TESTIDS.contactPostcode}
                    placeholder="CV32"
                    className={inputClass}
                  />
                </Field>

                <Field label="Service needed" className="sm:col-span-2">
                  <select
                    value={form.service}
                    onChange={update('service')}
                    data-testid={TESTIDS.contactService}
                    className={inputClass}
                  >
                    <option value="">Select a service…</option>
                    {SERVICES.map((s) => (
                      <option key={s.title} value={s.title}>
                        {s.title}
                      </option>
                    ))}
                    <option value="Other">Other / not sure</option>
                  </select>
                </Field>

                <Field label="Tell us about the job *" className="sm:col-span-2">
                  <textarea
                    required
                    rows={5}
                    value={form.message}
                    onChange={update('message')}
                    data-testid={TESTIDS.contactMessage}
                    placeholder="e.g. Two-storey semi, gutters overflowing at the front..."
                    className={`${inputClass} resize-y`}
                  />
                </Field>
              </div>

              <button
                type="submit"
                disabled={status.state === 'loading'}
                data-testid={TESTIDS.contactSubmit}
                className="mt-8 inline-flex items-center justify-center gap-2 bg-[#E8B842] hover:bg-[#F4C534] disabled:opacity-60 text-black font-display font-bold uppercase tracking-wider px-8 py-4 transition-all hover:scale-[1.01] w-full sm:w-auto"
              >
                {status.state === 'loading' ? 'Sending…' : 'Send my enquiry'}
                <Send className="w-4 h-4" />
              </button>

              {status.state === 'success' && (
                <div
                  data-testid={TESTIDS.contactSuccess}
                  className="mt-6 flex items-start gap-3 p-4 border border-[#E8B842] bg-[#E8B842]/10 text-[#FDFBF7]"
                >
                  <CheckCircle2 className="w-5 h-5 text-[#E8B842] mt-0.5 shrink-0" />
                  <span>{status.msg}</span>
                </div>
              )}
              {status.state === 'error' && (
                <div
                  data-testid={TESTIDS.contactError}
                  className="mt-6 flex items-start gap-3 p-4 border border-red-500/40 bg-red-500/10 text-red-200"
                >
                  <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
                  <span>{status.msg}</span>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

const inputClass =
  'w-full bg-[#1A1A1A] border border-[#2a2a2a] text-white px-4 py-3 focus:outline-none focus:border-[#E8B842] focus:ring-1 focus:ring-[#E8B842] transition-all placeholder:text-[#5a5a5a]';

const Field = ({ label, children, className = '' }) => (
  <label className={`flex flex-col gap-2 ${className}`}>
    <span className="font-display uppercase tracking-widest text-xs text-[#A3A3A3]">
      {label}
    </span>
    {children}
  </label>
);

export default Contact;
